import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { collection, query, where, getDocs } from 'firebase/firestore';
import * as HeroIcons from '@heroicons/react/24/outline';

const ParentLoginPage = ({ schoolSettings, onLogin, onBackToLanding, db, appId }) => {
    const [studentId, setStudentId] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        if (!studentId || !password) {
            setError('الرجاء إدخال الرقم الدراسي وكلمة المرور.');
            return;
        }
        
        if (studentId.length < 8) {
            setError('يجب أن يكون الرقم الدراسي 8 أرقام على الأقل.');
            return;
        }

        setIsLoading(true);
        try {
            // First, find the student with the given ID
            const studentsRef = collection(db, `artifacts/${appId}/public/data/students`);
            const q = query(studentsRef, where('studentId', '==', studentId));
            const querySnapshot = await getDocs(q);

            if (querySnapshot.empty) {
                setError('رقم الطالب غير موجود.');
                setIsLoading(false);
                return;
            }

            const studentDoc = querySnapshot.docs[0];
            const studentData = studentDoc.data();

            // Verify the parent's phone number
            if (studentData.parentPhone !== password) {
                setError('كلمة المرور غير صحيحة.');
                setIsLoading(false);
                return;
            }

            // If authentication is successful, fetch all students associated with this parentPhone
            const childrenQuery = query(studentsRef, where('parentPhone', '==', password));
            const childrenSnapshot = await getDocs(childrenQuery);
            const childrenStudents = childrenSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            if (onLogin) {
                onLogin({ 
                    studentId, 
                    studentData: {
                        id: studentDoc.id,
                        ...studentData
                    },
                    childrenStudents: childrenStudents // Pass all children associated with this parent
                });
            }

        } catch (error) {
            console.error('Login error:', error);
            setError('حدث خطأ أثناء تسجيل الدخول. الرجاء المحاولة مرة أخرى.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-100 flex flex-col justify-center items-center p-4" dir="rtl">
            <div className="w-full max-w-md">
                <div className="bg-white p-8 rounded-2xl shadow-lg text-center">
                    <img src={schoolSettings?.schoolLogo || '/logo192.png'} alt="شعار المدرسة" className="w-24 h-24 rounded-full mx-auto mb-4 border-4 border-blue-200" />
                    <h1 className="text-2xl font-bold text-gray-800 mb-2">بوابة ولي الأمر</h1>
                    <p className="text-gray-500 mb-6">مرحباً بك! الرجاء تسجيل الدخول لمتابعة ملف ابنك/ابنتك.</p>
                    <form onSubmit={handleLogin} className="space-y-4">
                        <div className="relative">
                            <div className="absolute right-3 top-3 text-gray-400">
                                <HeroIcons.UserIcon className="h-6 w-6" />
                            </div>
                            <input 
                                type="text" 
                                placeholder="الرقم الدراسي للطالب" 
                                value={studentId}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/[^0-9]/g, '');
                                    setStudentId(value);
                                }}
                                pattern="[0-9]{8,}"
                                minLength={8}
                                title="يجب أن يكون الرقم الدراسي 8 أرقام على الأقل"
                                className="p-3 pr-12 border rounded-lg w-full text-right focus:ring-2 focus:ring-blue-500 transition"
                                required
                            />
                        </div>
                        <div className="relative">
                            <div className="absolute right-3 top-3 text-gray-400">
                                <HeroIcons.LockClosedIcon className="h-6 w-6" />
                            </div>
                            <input 
                                type="password" 
                                placeholder="كلمة مرور الولي" 
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="p-3 pr-12 border rounded-lg w-full text-right focus:ring-2 focus:ring-blue-500 transition"
                                required
                            />
                        </div>
                        {error && (
                            <div className="bg-red-50 border-r-4 border-red-400 p-3 rounded-lg text-right">
                                <p className="text-red-700 text-sm">{error}</p>
                            </div>
                        )}
                        <button 
                            type="submit" 
                            disabled={isLoading} 
                            className="w-full bg-blue-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-blue-700 transition flex items-center justify-center disabled:bg-blue-300 group"
                        >
                            {isLoading ? (
                                <>
                                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <span className="mr-2">جاري تسجيل الدخول...</span>
                                </>
                            ) : (
                                <>
                                    <HeroIcons.KeyIcon className="h-5 w-5 opacity-75 group-hover:scale-110 transition-transform" />
                                    <span className="mr-2">تسجيل الدخول</span>
                                </>
                            )}
                        </button>
                    </form>
                </div>
            </div>
            <button 
                onClick={onBackToLanding} 
                className="mt-4 text-blue-600 hover:text-blue-800 transition-colors"
            >
                العودة للصفحة الرئيسية
            </button>
            <div className="w-full max-w-md mt-6">
                <div className="bg-blue-50 border-l-4 border-blue-400 text-blue-800 p-4 rounded-r-lg shadow-sm mb-4" style={{borderTopRightRadius: '8px', borderBottomRightRadius: '8px'}}>
                    <div className="flex">
                        <div className="py-1"><HeroIcons.KeyIcon className="h-6 w-6 text-blue-500 mr-4"/></div>
                        <div>
                            <p className="font-bold">تعليمات الدخول</p>
                            <p className="text-sm">استخدم الرقم الدراسي الخاص بالطالب وكلمة مرور الولي للدخول.</p>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

ParentLoginPage.propTypes = {
    schoolSettings: PropTypes.shape({
        schoolLogo: PropTypes.string
    }).isRequired,
    onLogin: PropTypes.func.isRequired,
    onBackToLanding: PropTypes.func.isRequired,
    db: PropTypes.object.isRequired,
    appId: PropTypes.string.isRequired
};

export default ParentLoginPage;
